import type { FonotekaTrack } from '@/api/client';
import {
  getFileTypeLabel,
  splitFileNameStemAndSuffix,
} from '@/components/ProjectFilesPopover/projectFilesPopoverUtils';
import { FonotekaAudioPlayer } from './FonotekaAudioPlayer';
import { FonotekaTrackMenu } from './FonotekaTrackMenu';
import { OverflowChips } from './OverflowChips';
import { Badge } from './ui/badge';
import { Card } from './ui/card';

export type FonotekaTrackRowProps = {
  track: FonotekaTrack;
  canWrite: boolean;
  canPickAuthor: boolean;
  onRename: () => void;
  onDelete: () => void;
  onOpenTagsCatalog: () => void;
  onToggleTag: (tagId: string, on: boolean) => void;
  onSetAuthor: (legalEntityId: string | null) => void;
  tagsSaving?: boolean;
  authorSaving?: boolean;
};

function TagPill({ name }: { name: string }) {
  return <Badge variant="outline">{name}</Badge>;
}

function ProjectPill({ name }: { name: string }) {
  return <Badge variant="default">{name}</Badge>;
}

export function FonotekaTrackRow({
  track,
  canWrite,
  canPickAuthor,
  onRename,
  onDelete,
  onOpenTagsCatalog,
  onToggleTag,
  onSetAuthor,
  tagsSaving,
  authorSaving,
}: FonotekaTrackRowProps) {
  const { stem } = splitFileNameStemAndSuffix(track.file_name);
  const ext = getFileTypeLabel(track.file_name);
  const displayName = stem || track.file_name;

  const tagItems = track.tags.map((tag) => <TagPill key={tag.id} name={tag.name} />);

  const projectItems = track.used_in_projects.map((p) => (
    <ProjectPill key={p.id} name={p.name} />
  ));

  return (
    <Card className="fonoteka-track-row">
      <div className="fonoteka-track-grid">
        <div className="flex min-w-0 items-center gap-[0.4rem] text-[0.8125rem] font-medium">
          {ext ? <Badge variant="secondary">{ext}</Badge> : null}
          <span className="truncate">{displayName}</span>
        </div>
        <OverflowChips items={tagItems} />
        <OverflowChips items={projectItems} />
        <FonotekaTrackMenu
          track={track}
          canWrite={canWrite}
          canPickAuthor={canPickAuthor}
          onRename={onRename}
          onDelete={onDelete}
          onOpenTagsCatalog={onOpenTagsCatalog}
          onToggleTag={onToggleTag}
          onSetAuthor={onSetAuthor}
          tagsSaving={tagsSaving}
          authorSaving={authorSaving}
        />
      </div>
      <FonotekaAudioPlayer
        instanceId={track.id}
        src={track.file_url}
        labelForA11y={displayName}
        packId={track.pack_id}
      />
    </Card>
  );
}
